import React, {memo, useMemo} from 'react';
import PropTypes from 'prop-types';
import {styled} from '@mui/material';
import {numberInUSFormat} from 'mui-core/utils';
import {useDrawingArea} from '@mui/x-charts/hooks';
import {PieChart, pieArcLabelClasses} from '@mui/x-charts/PieChart';

export const valueFormatter = item => `${item.value}%`;

const StyledText = styled('text')(({theme}) => ({
  fontSize: 26,
  fontWeight: 600,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: 'light-dark(#000, #fff)',
}));

function PieCenterLabel({children}) {
  const {width, height, left, top} = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}

const colorPalettes = [
  '#800080',
  '#9ACD32',
  '#1E3A8A',
  '#F59E0B',
  '#EF4444',
  '#6366F1',
  '#14B8A6',
  '#A52A2A',
  '#B8860B',
  '#E9967A',
  '#808000',
  '#DB7093',
];

function SVGRectangle({className, color}) {
  return (
    <svg viewBox="0 0 10 10" className={className}>
      <rect width="10" height="10" fill={color} />
    </svg>
  );
}

const PieChartComponent = ({
  centerLabel,
  width = 200,
  height = 200,
  generateColors,
  chartData = [],
  labelWithCount = false,
  colors = ['#1E3A8A', '#F59E0B', '#EF4444', '#6366F1', '#14B8A6', '#A52A2A'],
}) => {
  const total = chartData?.reduce((sum, item) => sum + item.value, 0) || 0;

  const percentageData = chartData?.map(item => ({
    label: labelWithCount
      ? `${item.label}(${numberInUSFormat(item.value)})`
      : item.label,
    value: Math.round((item.value / total) * 100),
    labelMarkType: SVGRectangle,
    ...(item.color && {color: item.color}),
  }));

  const data = {
    data: percentageData,
    valueFormatter,
  };

  const generateColorsList = useMemo(() => {
    const shuffled = [...colorPalettes].sort(
      () => Math.random() - 0.5 + Math.random() * 0.1
    );
    return shuffled.slice(0, chartData.length);
  }, []);

  return (
    <PieChart
      width={width}
      height={height}
      colors={generateColors ? generateColorsList : colors}
      series={[
        {
          fontFamily: 'Inter',
          arcLabelMinAngle: 20,
          arcLabelRadius: '60%',
          arcLabel: item => `${item.value}%`,
          ...data,
          innerRadius: centerLabel && 80,
        },
      ]}
      sx={{
        [`& .${pieArcLabelClasses.root}`]: {
          fill: '#fff',
          fontWeight: 'bold',
          fontFamily: 'Inter',
        },
        '& .MuiChartsLabel-root.MuiChartsLegend-label': {
          fontFamily: 'Inter',
          lineHeight: 'normal',
        },
        '& .MuiChartsLegend-root': {
          width: '50%',
          gap: {xs: chartData.length > 5 ? 1 : 2, md: 1.2},
        },
        '& .MuiChartsLabelMark-root svg': {
          width: '15px',
        },
        '& .MuiPieChart-seriesLabels': {
          display: centerLabel ? 'none' : 'block',
        },
      }}>
      {centerLabel && (
        <PieCenterLabel>{numberInUSFormat(centerLabel)}</PieCenterLabel>
      )}
    </PieChart>
  );
};

PieChartComponent.prototype = {
  width: PropTypes.number,
  height: PropTypes.number,
  chartData: PropTypes.array,
  centerLabel: PropTypes.bool,
  labelWithCount: PropTypes.bool,
  generateColors: PropTypes.bool,
};

export default memo(PieChartComponent);
